import React from 'react';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import {
  RadioGroupControl,
  TextV2,
  SvgSearchRegular,
} from '@insights-ltd/design-library/components';
import { TranslationKeys } from 'types/api';
import { styled } from '@mui/material';
import TreeView from 'components/TreeView';

type SearchPanelProps = {
  translationCategoryKeys: TranslationKeys;
  onSubmit: (newSearchText: string, newFilters: string[]) => void;
};

const StyledForm = styled('form')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const SearchPanel = ({
  translationCategoryKeys,
  onSubmit,
}: SearchPanelProps) => {
  const [searchText, setSearchText] = React.useState('');
  const [filters, setFilters] = React.useState<string[]>([]);
  const [treeFilter, setTreeFilter] = React.useState<string>(
    Object.keys(translationCategoryKeys || {})[0] || '',
  );

  const keysObject =
    treeFilter === ''
      ? translationCategoryKeys
      : (translationCategoryKeys[treeFilter] as TranslationKeys);

  const filterOptions = Object.keys(translationCategoryKeys).map((item) => ({
    value: item,
    label: item,
  }));
  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();
    onSubmit(searchText, filters);
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <Paper sx={(theme) => ({ marginTop: theme.spacing(1) })} elevation={0}>
        <Box
          sx={(theme) => ({
            marginTop: 1,
            marginBottom: 1,
            paddingRight: theme.spacing(3),
            paddingLeft: theme.spacing(3),
          })}
        >
          <TextV2 variant="bodyBold">Search strings</TextV2>
        </Box>
        <Box
          sx={{
            pl: 4,
            pr: 4,
          }}
        >
          <Grid
            container
            sx={(theme) => ({
              border: `1px solid ${theme.palette.grey[500]}`,
              borderRadius: '4px',
            })}
          >
            <Grid size={4}>
              <Box
                sx={{
                  p: 2,
                }}
              >
                <TextV2 variant="bodyBold" color="textSecondary">
                  Select a category
                </TextV2>
                <RadioGroupControl
                  options={filterOptions}
                  name="roles"
                  title="Select a cateogry"
                  value={treeFilter}
                  onChange={setTreeFilter}
                />
              </Box>
            </Grid>
            <Grid
              sx={(theme) => ({
                padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
                overflow: 'auto',
                backgroundColor: theme.palette.background.default,
                borderLeft: `1px solid ${theme.palette.grey[500]}`,
                borderRadius: '0 4px 4px 0',
              })}
              size={8}
            >
              <TextV2 variant="bodyBold" color="textSecondary">
                Filter selected category
              </TextV2>
              <TreeView
                keysObject={keysObject}
                path={treeFilter}
                handleSelected={setFilters}
              />
            </Grid>
          </Grid>
        </Box>
        <Grid
          container
          spacing={5}
          sx={(theme) => ({
            alignItems: 'center',
            padding: theme.spacing(3),
          })}
        >
          <Grid>
            <Container
              disableGutters
              sx={(theme) => ({
                display: 'flex',
                width: '300px',
                height: '50px',
                border: `1px solid ${theme.palette.grey[600]}`,
                borderRadius: theme.spacing(0.4),
              })}
            >
              <IconButton
                type="submit"
                sx={(theme) => ({ padding: theme.spacing(1) })}
                size="large"
              >
                <SvgSearchRegular />
              </IconButton>
              <InputBase
                placeholder="Enter term..."
                inputProps={{ 'aria-label': 'Enter Search Term' }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                type="search"
              />
            </Container>
          </Grid>
          <Grid>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              aria-label="Search Panel Search Button"
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </StyledForm>
  );
};

export default SearchPanel;
